import React, { useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import UsageOverviewPanel from "./UsageOverviewPanel";
import PlanFeaturesPanel from "./PlanFeaturesPanel";
import UpgradeRequestPanel from "./UpgradeRequestPanel";
import AutoScalePanel from "./AutoScalePanel";
import ScraperLimitsPanel from "./ScraperLimitsPanel";
import InvoicesPanel from "./InvoicesPanel";

type Tab = "usage" | "plan" | "autoscale" | "upgrade" | "scraper" | "invoices";

export default function BillingPage() {
  const api = useApi();
  const [activeTab, setActiveTab] = useState<Tab>("usage");
  const [openingPortal, setOpeningPortal] = useState(false);

  const tabClass = (tab: Tab) =>
    `pb-2 text-sm font-medium border-b-2 whitespace-nowrap ${
      activeTab === tab
        ? "border-cornflower-blue-500 text-cornflower-blue-600"
        : "border-transparent text-slate-500 hover:text-slate-700"
    }`;

  const handleManagePayment = useCallback(async () => {
    setOpeningPortal(true);
    try {
      const resp = await api.messaging.get("admin/billing/portal");
      if (resp.ok) {
        const { url } = await resp.json();
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      }
    } catch {
      // Ignore — Stripe portal not configured for this account
    } finally {
      setOpeningPortal(false);
    }
  }, [api.messaging]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-slate-200 px-6 pt-4">
        <div className="flex items-start justify-between mb-3">
          <h1 className="text-lg font-semibold text-slate-gray-800">Billing &amp; Usage</h1>
          <button
            onClick={handleManagePayment}
            disabled={openingPortal}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-gray-200 text-xs font-medium text-slate-gray-600 hover:bg-slate-gray-50 disabled:opacity-50 transition-colors"
          >
            {openingPortal ? (
              <span className="animate-spin h-3 w-3 border border-slate-gray-400 border-t-transparent rounded-full" />
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            )}
            Manage Payment Method
          </button>
        </div>
        <div className="flex gap-4 overflow-x-auto">
          <button className={tabClass("usage")} onClick={() => setActiveTab("usage")}>
            Usage Overview
          </button>
          <button className={tabClass("plan")} onClick={() => setActiveTab("plan")}>
            Plan &amp; Features
          </button>
          <button className={tabClass("autoscale")} onClick={() => setActiveTab("autoscale")}>
            Auto-Scale &amp; Alerts
          </button>
          <button className={tabClass("invoices")} onClick={() => setActiveTab("invoices")}>
            Invoices
          </button>
          <button className={tabClass("upgrade")} onClick={() => setActiveTab("upgrade")}>
            Contact Account Manager
          </button>
          <button className={tabClass("scraper")} onClick={() => setActiveTab("scraper")}>
            Web Scraper
          </button>
        </div>
      </div>
      {activeTab === "usage" && <UsageOverviewPanel />}
      {activeTab === "plan" && <PlanFeaturesPanel />}
      {activeTab === "autoscale" && <AutoScalePanel />}
      {activeTab === "invoices" && <InvoicesPanel />}
      {activeTab === "upgrade" && (
        <div className="p-6">
          <UpgradeRequestPanel />
        </div>
      )}
      {activeTab === "scraper" && <ScraperLimitsPanel />}
    </div>
  );
}
